/**
 * Runtime configuration — fetched from /config.json at app boot.
 *
 * The same built artifact is deployed to dev, stg, and prd; the config.json
 * served at each environment carries the correct values. This avoids
 * build-per-environment (which was the StatsMag pattern) and makes rollbacks
 * safe: an older artifact with a fresh config still works.
 */

export type RuntimeConfig = {
  apiBaseUrl: string;
  environment: "dev" | "stg" | "prd";
  /**
   * Local-only escape hatch. When true, AuthProvider skips Firebase
   * initialization and presents a synthetic user; apiFetch sends no
   * Authorization header. The backend must run with AUTH_DEV_BYPASS=true
   * for this to actually let requests through. Refused at boot in prd.
   */
  devAuthBypass?: boolean;
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    appId: string;
  };
};

let cached: RuntimeConfig | null = null;

export async function loadConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  const response = await fetch("/config.json", { cache: "no-cache" });
  if (!response.ok) {
    throw new Error(`Failed to load /config.json: ${response.status}`);
  }
  const raw = (await response.json()) as RuntimeConfig;
  if (raw.environment === "prd" && raw.devAuthBypass) {
    throw new Error("devAuthBypass must not be enabled in prd. Refusing to boot.");
  }
  cached = raw;
  return cached;
}

export function getConfig(): RuntimeConfig {
  if (!cached) {
    throw new Error("Config not loaded. Call loadConfig() at app boot.");
  }
  return cached;
}
